import React, { useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";
import { useNavigate } from "react-router-dom";
import api from "../../../services/apiService";
import {
  downloadRelatorioPerfilEleitorado,
} from "../../../services/downloadRelatorioService";
import BotaoSalvar from "../../../components/botao-salvar";
import MensagemErroForm from "../../../components/mensagem-erro-form";
import SeletorFiltrosPerfilEleitorado from "../../../components/seletor-filtros-perfil-eleitorado";
import styles from "./styles.module.css";

const opcoesAgrupamento = [
  { value: "local_votacao", label: "Local de Votação" },
  { value: "zona", label: "Zona" },
  { value: "municipio", label: "Município" },
];

const opcoesMetricaRanking = [
  { value: "quantidade_eleitores", label: "Quantidade de Eleitores" },
  { value: "percentual_no_agrupamento", label: "Percentual no Agrupamento" },
  {
    value: "percentual_no_total_filtrado",
    label: "Percentual no Total Filtrado",
  },
];

const opcoesDirecaoRanking = [
  { value: "desc", label: "Decrescente" },
  { value: "asc", label: "Crescente" },
];

const FiltroRelatorioPerfilEleitoradoPage = () => {
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  const [modoExibicao, setModoExibicao] = useState("visualizar");
  const [agrupamento, setAgrupamento] = useState("local_votacao");
  const [metricaRanking, setMetricaRanking] = useState("quantidade_eleitores");
  const [direcaoRanking, setDirecaoRanking] = useState("desc");
  const [considerarNaoInformados, setConsiderarNaoInformados] = useState(true);
  const [filtrosPerfil, setFiltrosPerfil] = useState({});

  const [estados, setEstados] = useState([]);
  const [siglaEstado, setSiglaEstado] = useState("");
  const [municipios, setMunicipios] = useState([]);
  const [municipioSelecionado, setMunicipioSelecionado] = useState({
    label: "Todos",
    value: null,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const carregarEstados = async () => {
      try {
        const response = await api.get(
          "/selects/listar-estados-com-perfil-eleitorado"
        );
        setEstados(response.estados || []);
      } catch (error) {
        setErro(error.message || "Erro ao carregar estados.");
      }
    };

    carregarEstados();
  }, []);

  useEffect(() => {
    setMunicipioSelecionado({ label: "Todos", value: null });
    setMunicipios([]);

    if (!siglaEstado) {
      return;
    }

    const carregarMunicipios = async () => {
      try {
        const response = await api.get(
          "/selects/listar-municipios-com-perfil-eleitorado",
          { params: { sigla_estado: siglaEstado } }
        );

        setMunicipios(response.municipios || []);
      } catch (error) {
        setErro(error.message || "Erro ao carregar municípios.");
      }
    };

    carregarMunicipios();
  }, [siglaEstado]);

  const opcoesMunicipios = useMemo(() => {
    const lista = municipios.map((item) => ({
      label: item.nome_municipio,
      value: item.id_municipio,
    }));

    return [{ label: "Todos", value: null }, ...lista];
  }, [municipios]);

  const buscarMunicipios = async (entrada) => {
    const texto = (entrada || "").toLowerCase().trim();

    if (!texto) {
      return opcoesMunicipios.slice(0, 100);
    }

    return opcoesMunicipios.filter((item) =>
      item.label.toLowerCase().includes(texto)
    );
  };

  const montarFiltrosPerfil = () => {
    return Object.entries(filtrosPerfil).reduce((acc, [categoria, ids]) => {
      if (Array.isArray(ids) && ids.length) {
        acc[categoria] = ids;
      }
      return acc;
    }, {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");

    if (!siglaEstado) {
      setErro("Selecione um estado.");
      return;
    }

    const filtrosPerfilLimpos = montarFiltrosPerfil();

    const payload = {
      tipo_agrupamento: agrupamento,
      sigla_estado: siglaEstado,
      id_municipio: municipioSelecionado?.value ?? null,
      considerar_nao_informados: considerarNaoInformados,
      metrica_ranking: metricaRanking,
      direcao_ranking: direcaoRanking,
      filtros_perfil: filtrosPerfilLimpos,
    };

    setLoading(true);

    try {
      if (modoExibicao === "download") {
        await downloadRelatorioPerfilEleitorado(payload);
      } else {
        const response = await api.post(
          "/perfil-eleitorado/perfil-agrupado",
          payload
        );

        const dados = Array.isArray(response) ? response : response?.dados || [];
        const perfilAplicado = Array.isArray(response)
          ? null
          : response?.perfil_aplicado || null;

        navigate("/relatorios/perfil-eleitorado/resultado", {
          state: {
            dados,
            tipo_agrupamento: agrupamento,
            perfil_aplicado: perfilAplicado,
          },
        });
      }
    } catch (error) {
      setErro(error.message || "Erro ao gerar relatório.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="formContainer">
      <h2 className="titulo">Relatório de Perfil do Eleitorado</h2>
      {erro && <MensagemErroForm erro={erro} />}

      <form onSubmit={handleSubmit}>
        <fieldset className="fieldset">
          <legend className="titulo">Filtro do Relatório</legend>

          <div className="formRow">
            <div className="formGroup">
              <label>Modo de Exibição:</label>
              <select
                value={modoExibicao}
                onChange={(event) => setModoExibicao(event.target.value)}
                required
              >
                <option value="visualizar">Visualizar na Tela</option>
                <option value="download">Baixar Excel</option>
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Tipo de Agrupamento:</label>
              <select
                value={agrupamento}
                onChange={(event) => setAgrupamento(event.target.value)}
                required
              >
                {opcoesAgrupamento.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Estado (UF):</label>
              <select
                value={siglaEstado}
                onChange={(event) => setSiglaEstado(event.target.value)}
                required
              >
                <option value="">Selecione o estado</option>
                {estados.map((estado) => (
                  <option
                    key={estado.id_estado || estado.sigla_estado}
                    value={estado.sigla_estado}
                  >
                    {estado.sigla_estado}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Município (opcional):</label>
              <AsyncSelect
                cacheOptions
                defaultOptions={opcoesMunicipios}
                loadOptions={buscarMunicipios}
                value={municipioSelecionado}
                onChange={(selected) => setMunicipioSelecionado(selected)}
                placeholder="Digite o nome do município"
                className={styles.asyncSelect}
                classNamePrefix="formSelect"
                isDisabled={!siglaEstado}
              />
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Métrica de Ranking:</label>
              <select
                value={metricaRanking}
                onChange={(event) => setMetricaRanking(event.target.value)}
                required
              >
                {opcoesMetricaRanking.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="formGroup">
              <label>Direção do Ranking:</label>
              <select
                value={direcaoRanking}
                onChange={(event) => setDirecaoRanking(event.target.value)}
                required
              >
                {opcoesDirecaoRanking.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Considerar não informados nos percentuais:</label>
              <select
                value={considerarNaoInformados ? "true" : "false"}
                onChange={(event) =>
                  setConsiderarNaoInformados(event.target.value === "true")
                }
                required
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <SeletorFiltrosPerfilEleitorado
                filtrosSelecionados={filtrosPerfil}
                setFiltrosSelecionados={setFiltrosPerfil}
              />
            </div>
          </div>
        </fieldset>

        <div className="actions">
          <BotaoSalvar label="Gerar Relatório" loading={loading} />
        </div>
      </form>
    </div>
  );
};

export default FiltroRelatorioPerfilEleitoradoPage;
